import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Sparkles, ArrowLeft, Users, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";

function formatCurrency(amount, currency) {
  try {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: (currency || "aud").toUpperCase(),
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `$${amount}`;
  }
}

const FEATURE_LIST = [
  "Every module — Grocery, Bills, Warranties, Plants and more",
  "Shared household — invite the whole family",
  "AI Scanners for receipts, invoices & business cards",
  "Daily push digests and reminders",
  "Priority support",
];

export default function Upgrade() {
  const nav = useNavigate();
  const [search] = useSearchParams();
  const { user, billing, refreshBilling } = useAuth();
  const [busy, setBusy] = useState(null); // "monthly" | "annual" | null
  const [portalBusy, setPortalBusy] = useState(false);
  const [cycle, setCycle] = useState("year"); // "month" | "year" — default annual for savings
  const plans = billing?.plans || [];

  useEffect(() => {
    refreshBilling();
    if (search.get("canceled")) {
      toast.info("Checkout canceled — no charges made.");
    }
  }, [refreshBilling, search]);

  const startCheckout = useCallback(async (plan) => {
    setBusy(plan);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan,
        billing_cycle: cycle,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start checkout.");
      setBusy(null);
    }
  }, [cycle]);

  const openBillingPortal = useCallback(async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/billing/portal", {
        return_url: window.location.origin + "/upgrade",
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't open the billing portal.");
      setPortalBusy(false);
    }
  }, []);

  const isActive = billing?.status === "active";

  const isExpired = billing?.status === "trial_expired";
  const daysLeft = billing?.trial_days_remaining;

  return (
    <div className="min-h-screen bg-hearth-cream text-hearth-ink px-6 py-10 sm:py-16">
      <div className="max-w-5xl mx-auto">
        <button
          data-testid="upgrade-back-btn"
          onClick={() => nav(-1)}
          className="inline-flex items-center gap-2 caption text-hearth-mute hover:text-hearth-ink mb-8"
        >
          <ArrowLeft size={14} /> Back
        </button>

        <div className="flex items-center gap-3 mb-4">
          <Logo size={36} />
        </div>

        {isExpired ? (
          <div data-testid="paywall-hero" className="mb-10">
            <p className="caption text-hearth-clayDark tracking-widest">Your trial has ended</p>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-2">
              Keep the house running.
            </h1>
            <p className="mt-3 text-hearth-mute max-w-xl leading-relaxed">
              You've had a full month to move in. Pick a plan below to keep adding to your groceries,
              bills, warranties and everything else. You can still browse everything you already had.
            </p>
          </div>
        ) : (
          <div className="mb-10">
            <p className="caption text-hearth-clayDark tracking-widest">Simple pricing</p>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-2">
              A quiet, warm home.
            </h1>
            <p className="mt-3 text-hearth-mute max-w-xl leading-relaxed">
              You're on a free trial for 30 days. When it's time, pick a plan that fits.
              {typeof daysLeft === "number" && (
                <> You have <span className="font-medium text-hearth-ink">{daysLeft} {daysLeft === 1 ? "day" : "days"}</span> remaining.</>
              )}
            </p>
          </div>
        )}

        {/* Cycle toggle — soft pill switch between monthly and annual */}
        <div className="mb-8 inline-flex items-center gap-1 p-1 rounded-full bg-hearth-stone border border-hearth-line">
          {["month", "year"].map((c) => (
            <button
              key={c}
              data-testid={`cycle-${c}`}
              type="button"
              onClick={() => setCycle(c)}
              className={`px-5 py-1.5 rounded-full text-sm font-medium transition ${
                cycle === c
                  ? "bg-white text-hearth-ink shadow-sm"
                  : "text-hearth-mute hover:text-hearth-ink"
              }`}
            >
              {c === "month" ? "Monthly" : "Annual"}
              {c === "year" && (
                <span className="ml-2 text-[10px] uppercase tracking-widest text-hearth-clayDark">save 20%</span>
              )}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="upgrade-plans">
          {plans.map((plan) => {
            const isYear = cycle === "year";
            const amount = isYear ? (plan.amount_annual ?? plan.amount) : plan.amount;
            const monthlyEquiv = isYear ? amount / 12 : amount;
            const isFeatured = plan.id === "table";  // couple tier — the middle sweet spot
            return (
              <div
                key={plan.id}
                data-testid={`plan-card-${plan.id}`}
                className={`relative rounded-3xl border p-7 bg-white flex flex-col ${
                  isFeatured
                    ? "border-hearth-clay/60 shadow-lg md:scale-[1.03] z-10"
                    : "border-hearth-line"
                }`}
              >
                {isFeatured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-hearth-clay text-white px-3 py-1 text-[10px] tracking-widest uppercase inline-flex items-center gap-1 whitespace-nowrap">
                    <Sparkles size={12} /> Most loved
                  </div>
                )}
                <div>
                  <p className="caption text-hearth-clayDark tracking-widest">{plan.label}</p>
                  <p className="text-sm text-hearth-mute mt-1">{plan.subtitle}</p>
                  <div className="mt-4 flex items-baseline gap-1.5">
                    <span className="font-display text-4xl font-semibold tracking-tight">
                      {formatCurrency(amount, plan.currency)}
                    </span>
                    <span className="text-hearth-mute text-sm">/ {isYear ? "year" : "month"}</span>
                  </div>
                  {isYear && (
                    <p className="text-xs text-hearth-mute mt-1">
                      ~{formatCurrency(Number(monthlyEquiv.toFixed(2)), plan.currency)} / month
                    </p>
                  )}
                  <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-hearth-mute bg-hearth-cream/60 px-3 py-1.5 rounded-full">
                    <Users size={12} />
                    {plan.seats === 1
                      ? "1 seat"
                      : `Up to ${plan.seats} members`}
                  </div>
                  <p className="mt-4 text-sm text-hearth-mute italic leading-relaxed">
                    {plan.tagline}
                  </p>
                </div>

                <ul className="mt-6 space-y-2.5 flex-1">
                  {FEATURE_LIST.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm">
                      <span className="mt-0.5 w-4 h-4 rounded-full bg-hearth-sage/25 grid place-items-center shrink-0">
                        <Check size={10} className="text-hearth-sageDark" />
                      </span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  data-testid={`plan-checkout-${plan.id}`}
                  onClick={() => startCheckout(plan.id)}
                  disabled={busy !== null}
                  className={`mt-6 w-full h-12 rounded-full font-medium ${
                    isFeatured
                      ? "bg-hearth-terracotta hover:bg-hearth-terracottaDark text-white"
                      : "bg-hearth-ink hover:bg-hearth-ink/90 text-white"
                  }`}
                >
                  {busy === plan.id ? <Loader2 className="animate-spin" size={18} /> : `Choose ${plan.label}`}
                </Button>
              </div>
            );
          })}
        </div>

        <p className="mt-8 text-xs text-hearth-mute/80 text-center">
          Prices in AUD. Cancel anytime. Powered by Stripe.
          {user?.email && <> · Signed in as <span className="font-mono">{user.email}</span></>}
        </p>

        {isActive && (
          <div className="mt-6 flex justify-center">
            <Button
              data-testid="billing-portal-btn"
              variant="ghost"
              onClick={openBillingPortal}
              disabled={portalBusy}
              className="rounded-full border border-hearth-line hover:bg-hearth-stone text-sm"
            >
              {portalBusy
                ? <Loader2 className="animate-spin mr-1.5" size={14} />
                : <ExternalLink size={14} className="mr-1.5" />}
              Manage or cancel my subscription
            </Button>
          </div>
        )}

        {!isExpired && (
          <p className="mt-2 text-xs text-hearth-mute text-center">
            <Link to="/" className="underline">Not yet — keep exploring the trial</Link>
          </p>
        )}
      </div>
    </div>
  );
}
